import { useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Upload, X, Loader2, PartyPopper, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google Ads conversion tracking.
// Paste the conversion LABEL from Google Ads here (the part AFTER the slash,
// e.g. for send_to "AW-18343366218/AbC-D_efG12" the label is "AbC-D_efG12").
const GADS_CONVERSION_ID = "AW-18343366218";
const GADS_CONVERSION_LABEL = "k1q1CKSmhdUcEMqc5qpE";

function fireAdsConversion(estimate) {
  if (typeof window === "undefined" || !window.gtag || !GADS_CONVERSION_LABEL) return;
  window.gtag("event", "conversion", {
    send_to: `${GADS_CONVERSION_ID}/${GADS_CONVERSION_LABEL}`,
    value: estimate || 0,
    currency: "USD",
  });
}

const inputClass = "h-12 rounded-xl border-slate-300 bg-white text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500";

const timelineOptions = [
  "Yes, within 1 month", "Yes, within 3 months", "Yes, within 6 months", "Just researching",
];

const systemOptions = [
  "Central Air Conditioner + Furnace", "Central Air Conditioner + Boiler", "Existing Heat Pump",
  "Ductless Mini-Split System", "Oil Heating System", "Natural Gas Heating System",
  "Propane Heating System", "Electric Baseboard Heating", "Window Air Conditioners",
  "No Existing Cooling System", "Other",
];

const emptyForm = {
  first_name: "", last_name: "", email: "", zip_code: "", timeline: "",
  property_address: "", square_footage: "", bedrooms: "", additional_rooms: "",
  floors: "", indoor_units: "", outdoor_units: "", current_system: "", equipment_model: "", utility_provider: "",
  notes: "",
};

const Field = ({ label, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-sm font-medium text-slate-700">{label}</Label>
    {children}
  </div>
);

export default function QuoteForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validStep1 = form.first_name && form.last_name && form.email && form.zip_code && form.timeline;

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => {
      const merged = [...prev, ...picked].slice(0, 6);
      return merged;
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    setSubmitting(true);
    try {
      const photos = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/leads/upload-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photos.push(data);
      }
      const { data: lead } = await axios.post(`${API}/leads`, { ...form, photos });
      setEstimate(lead.estimate ?? null);
      setDone(true);
      fireAdsConversion(lead.estimate);
    } catch (err) {
      toast.error("Something went wrong submitting your request. Please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="quote-confirmation">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <PartyPopper className="h-8 w-8" />
        </div>
        <h3 className="mt-6 font-heading text-2xl font-bold text-slate-950">Thank you for your interest.</h3>
        {estimate ? (
          <div className="mx-auto mt-6 max-w-md rounded-2xl border border-blue-200 bg-blue-50 p-6" data-testid="instant-estimate">
            <p className="text-sm font-medium text-blue-700">Your instant rough estimate</p>
            <p className="mt-1 font-heading text-4xl font-bold text-blue-700" data-testid="estimate-amount">${estimate.toLocaleString()}</p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              All-in estimate for equipment &amp; installation — indoor units, condensers, lines, line
              covers, and pads for outside units. Typically within ~10% of the final onsite price.
            </p>
          </div>
        ) : null}
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-600">
          We're building a faster, simpler way to shop for heat pumps online. Your information will
          help us evaluate equipment options, estimate pricing, and improve the platform before
          launch. We'll notify you when early access becomes available in your area.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-10" data-testid="quote-form">
      {/* Progress */}
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between text-sm font-medium text-slate-500">
          <span>Step {step} of 3</span>
          <span>{["Contact", "Your Home", "Photos"][step - 1]}</span>
        </div>
        <Progress value={(step / 3) * 100} className="h-2" />
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="First Name" testid="field-first-name">
                <Input className={inputClass} value={form.first_name} onChange={set("first_name")} placeholder="Jane" data-testid="input-first-name" />
              </Field>
              <Field label="Last Name" testid="field-last-name">
                <Input className={inputClass} value={form.last_name} onChange={set("last_name")} placeholder="Doe" data-testid="input-last-name" />
              </Field>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Email Address" testid="field-email">
                <Input type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="jane@email.com" data-testid="input-email" />
              </Field>
              <Field label="ZIP Code" testid="field-zip">
                <Input className={inputClass} value={form.zip_code} onChange={set("zip_code")} placeholder="02108" data-testid="input-zip" />
              </Field>
            </div>
            <Field label="Are you considering a heat pump installation or replacement within the next 6 months?" testid="field-timeline">
              <RadioGroup value={form.timeline} onValueChange={set("timeline")} className="grid gap-3 sm:grid-cols-2" data-testid="radio-timeline">
                {timelineOptions.map((opt) => (
                  <label key={opt} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 text-sm transition-colors ${form.timeline === opt ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`timeline-${opt}`}>
                    <RadioGroupItem value={opt} />
                    <span className="font-medium text-slate-700">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            </Field>
            <Button onClick={() => setStep(2)} disabled={!validStep1} className="h-12 w-full rounded-full bg-blue-600 text-base font-medium text-white hover:bg-blue-700 disabled:opacity-50" data-testid="step1-continue">
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="space-y-5">
            <h3 className="font-heading text-xl font-semibold text-slate-950">Tell Us About Your Home</h3>
            <Field label="Property Address" testid="field-address">
              <Input className={inputClass} value={form.property_address} onChange={set("property_address")} placeholder="123 Main St, Boston, MA" data-testid="input-address" />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Square Footage to Heat/Cool (Approximate)" testid="field-sqft">
                <Input className={inputClass} value={form.square_footage} onChange={set("square_footage")} placeholder="1800" data-testid="input-sqft" />
              </Field>
              <Field label="Number of Bedrooms" testid="field-bedrooms">
                <Input className={inputClass} value={form.bedrooms} onChange={set("bedrooms")} placeholder="3" data-testid="input-bedrooms" />
              </Field>
            </div>
            <Field label="Number of Additional Rooms / Finished Spaces" testid="field-rooms">
              <Input className={inputClass} value={form.additional_rooms} onChange={set("additional_rooms")} placeholder="2" data-testid="input-rooms" />
            </Field>
            <Field label="Preferred Number of Outdoor Units / Condensers" testid="field-outdoor-units">
              <Input className={inputClass} value={form.outdoor_units} onChange={set("outdoor_units")} placeholder="1" data-testid="input-outdoor-units" />
            </Field>
            <Field label="Would the indoor units be located on one floor or two floors?" testid="field-floors">
              <RadioGroup value={form.floors} onValueChange={set("floors")} className="grid grid-cols-2 gap-3" data-testid="radio-floors">
                {["One Floor", "Two Floors"].map((opt) => (
                  <label key={opt} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 text-sm transition-colors ${form.floors === opt ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`floors-${opt}`}>
                    <RadioGroupItem value={opt} />
                    <span className="font-medium text-slate-700">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            </Field>
            <Field label="Total Number of Indoor Units / Rooms Requiring Heating & Cooling" testid="field-units">
              <Input className={inputClass} value={form.indoor_units} onChange={set("indoor_units")} placeholder="4" data-testid="input-units" />
            </Field>
            <Field label="What type of heating and cooling system do you currently have?" testid="field-system">
              <Select value={form.current_system} onValueChange={set("current_system")}>
                <SelectTrigger className={inputClass} data-testid="select-system"><SelectValue placeholder="Select your current system" /></SelectTrigger>
                <SelectContent>
                  {systemOptions.map((opt) => (
                    <SelectItem key={opt} value={opt} data-testid={`system-${opt}`}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Existing Equipment Model Number (Optional)" testid="field-model">
              <Input className={inputClass} value={form.equipment_model} onChange={set("equipment_model")} placeholder="e.g. 24ANB660A003" data-testid="input-model" />
              <p className="text-xs text-slate-500">If available, enter the model number from your current outdoor unit, furnace, air handler, boiler, or heat pump.</p>
            </Field>
            <Field label="Utility Provider" testid="field-utility">
              <Input className={inputClass} value={form.utility_provider} onChange={set("utility_provider")} placeholder="e.g. National Grid" data-testid="input-utility" />
            </Field>
            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} variant="outline" className="h-12 rounded-full border-slate-300 px-6 text-base font-medium hover:bg-slate-100" data-testid="step2-back">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={() => setStep(3)} className="h-12 flex-1 rounded-full bg-blue-600 text-base font-medium text-white hover:bg-blue-700" data-testid="step2-continue">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="space-y-5">
            <div>
              <h3 className="font-heading text-xl font-semibold text-slate-950">Help Us Provide a More Accurate Estimate</h3>
              <p className="mt-2 text-sm text-slate-600">Photos help us understand your existing equipment and may reduce or eliminate the need for an in-home visit.</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-700">Please upload up to 6 photos:</p>
              <p className="mt-2"><span className="font-medium text-slate-700">Required:</span> existing outdoor equipment, existing indoor equipment (furnace, boiler, air handler, or mini-split)</p>
              <p className="mt-1"><span className="font-medium text-slate-700">Recommended:</span> electrical panel, thermostat, ductwork (if applicable), equipment model number label</p>
            </div>

            <div
              onClick={() => fileRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
              data-testid="photo-dropzone">
              <Upload className="h-8 w-8 text-blue-600" />
              <p className="mt-3 text-sm font-medium text-slate-700">Click to upload photos</p>
              <p className="text-xs text-slate-500">{files.length}/6 selected · JPG, PNG up to 12MB</p>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} disabled={files.length >= 6} data-testid="photo-input" />
            </div>

            {files.length > 0 && (
              <div className="grid grid-cols-3 gap-3" data-testid="photo-preview-grid">
                {files.map((f, i) => (
                  <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200">
                    <img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" />
                    <button onClick={() => removeFile(i)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/70 text-white" data-testid={`remove-photo-${i}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Field label="Notes or Questions (Optional)" testid="field-notes">
              <Textarea
                className="min-h-[110px] rounded-xl border-slate-300 bg-white text-base focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                value={form.notes}
                onChange={set("notes")}
                placeholder="Anything specific you'd like us to know? Ask questions about equipment, timing, rebates, or your home..."
                data-testid="input-notes"
              />
            </Field>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" disabled={submitting} className="h-12 rounded-full border-slate-300 px-6 text-base font-medium hover:bg-slate-100" data-testid="step3-back">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={submit} disabled={submitting} className="h-12 flex-1 rounded-full bg-emerald-500 text-base font-medium text-white hover:bg-emerald-600 disabled:opacity-60" data-testid="reserve-spot-button">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>) : (<><Check className="mr-2 h-4 w-4" /> Provide my Quote</>)}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
